import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

/**
 * Custom Tooltip Component for Joyride
 * Uses MUI components for consistent styling with the rest of the app
 */
export default function CustomTooltip({
    continuous,
    index,
    step,
    size,
    isLastStep,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    const progress = ((index + 1) / size) * 100;

    // Get custom tooltip styles from step if provided
    const tooltipStyles = step.styles?.tooltip || {};

    return (
        <Box
            {...tooltipProps}
            sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
                maxWidth: tooltipStyles.maxWidth || 400,
                minWidth: tooltipStyles.minWidth || 290,
                width: tooltipStyles.width,
                overflow: 'hidden',
            }}
        >
            {/* Progress Bar */}
            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    height: 4,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.secondary.main,
                    },
                }}
            />

            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, pt: 1.5 }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                >
                    {t('tutorial.stepProgress', {
                        current: index + 1,
                        total: size,
                        defaultValue: `${index + 1} / ${size}`,
                    })}
                </Typography>

                <IconButton
                    {...closeProps}
                    size="small"
                    sx={{
                        color: theme.palette.grey[500],
                        '&:hover': {
                            color: theme.palette.grey[700],
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>

            {/* Content */}
            <Box sx={{ px: 2, py: 1.5 }}>
                {step.title && (
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                        color="text.primary"
                    >
                        {step.title}
                    </Typography>
                )}

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                >
                    {step.content}
                </Typography>
            </Box>

            {/* Footer Actions */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                }}
            >
                {/* Skip Button */}
                <Button
                    {...skipProps}
                    size="small"
                    sx={{
                        color: theme.palette.grey[600],
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[100],
                        },
                    }}
                >
                    {t('tutorial.skip')}
                </Button>

                {/* Navigation Buttons */}
                <Stack direction="row" spacing={1}>
                    {index > 0 && (
                        <Button
                            {...backProps}
                            size="small"
                            color="secondary"
                            variant="contained"
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            {t('tutorial.back')}
                        </Button>
                    )}

                    {continuous && (
                        <Button
                            {...primaryProps}
                            size="small"
                            variant="contained"
                            color="secondary"
                            sx={{
                                textTransform: 'none',
                            }}
                            disableFocusRipple
                            disableRipple
                            disableElevation
                        >
                            {isLastStep ? t('tutorial.last') : t('tutorial.next')}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}
